import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage, FormattedNumber, injectIntl, useIntl } from 'react-intl';

import intlShape from '@/shared/utils/intlShape';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import RelationName from '@/shared/components/RelationName';
import FormattedListValue from '@/shared/components/FormattedListValue';
import { mapAssignmentGroupName, mapResourceName } from '@/shared/services/ObjectDisplayService';
import DataCacheService from '@/shared/services/DataCacheService';
import { guid1 } from '@/shared/utils/guid';
import messages from '../../reportingMessages';
import FormattedDateShort from '../../../../shared/components/FormattedDateShort';
import { columnStatusShape } from '../helpers';
import RelationHandlingLevel from '@/shared/components/RelationHandlingLevel';

const AmountColumn = ({ value, className }) => (
  <td
    className={classnames(
      'text-right amount',
      { 'text-muted': !value, 'text-danger': value < 0 },
      className,
    )}
  >
    {!value && value !== 0 ? null : <FormattedCurrency value={value} />}
  </td>
);
const HoursColumn = ({ value = null, className }) => (
  <td
    className={classnames(
      'text-right amount',
      { 'text-muted': !value, 'text-danger': value < 0 },
      className,
    )}
  >
    {value === null ? null : value.toTime()}
  </td>
);
const QuantityColumn = ({ value = null, className }) => (
  <td
    className={classnames(
      'text-right amount',
      { 'text-muted': !value, 'text-danger': value < 0 },
      className,
    )}
  >
    {value === null ? null : <FormattedNumber value={value} maximumFractionDigits={5} />}
  </td>
);
class RegistrationsPerRow extends Component {
  renderRelationName() {
    const {
      rowData: {
        relationId: id,
        relationDisplayName: name,
        relationType: type,
        relationCrm: crm,
        relationAlias: alias,
        relationIsActive: isActive,
        relationHandlingLevel,
        relationHandlingLevelClarification,
      } = {},
    } = this.props;
    const relation = {
      id,
      name,
      type,
      crm,
      alias,
      isActive,
    };
    return (
      <span>
        <RelationHandlingLevel
          handlingLevel={relationHandlingLevel}
          clarification={relationHandlingLevelClarification}
        />
        <RelationName relation={relation} />
      </span>
    );
  }

  renderAssignment() {
    const {
      intl,
      rowData: { assignmentName, assignmentTemplateName, assignmentTemplateId } = {},
    } = this.props;
    return mapAssignmentGroupName(
      assignmentTemplateId,
      assignmentTemplateName,
      assignmentName,
      intl,
    );
  }

  renderDetailColumn() {
    const { rowData: { registrationDate } = {} } = this.props;
    return !!registrationDate && <FormattedDateShort value={registrationDate} />;
  }

  renderResourceType() {
    const { rowData: { resourceType } = {} } = this.props;
    return <FormattedListValue list="resourcetype" id={resourceType} />;
  }

  renderResourceCategory() {
    const { rowData: { categoryId, categoryName } = {} } = this.props;
    return <FormattedListValue list="resourcecategory" id={categoryId} fallback={categoryName} />;
  }

  renderResource() {
    const {
      rowData: {
        categoryId,
        categoryName,
        resourceId: id,
        resourceName: name,
        isDefaultResource: isDefault,
      } = {},
      intl,
    } = this.props;

    return mapResourceName({ id, name, isDefault, categoryId, categoryName }, intl);
  }

  renderTaskTemplate() {
    const { rowData: { taskTemplateId } = {} } = this.props;
    return taskTemplateId ? (
      <FormattedListValue list="tasktemplate" id={taskTemplateId} />
    ) : (
      <span className="italic">
        <FormattedMessage {...messages.NotRegisteredOnTask} />
      </span>
    );
  }

  renderSubTaskTemplate() {
    const { rowData: { subTaskTemplateId } = {} } = this.props;
    return subTaskTemplateId ? (
      <FormattedListValue list="tasktemplate" id={subTaskTemplateId} />
    ) : (
      <span className="italic">
        <FormattedMessage {...messages.NotRegisteredOnSubTask} />
      </span>
    );
  }

  renderUserName() {
    const { rowData: { userId } = {}, intl } = this.props;
    if (!userId) return null;
    if (userId === guid1) return intl.formatMessage(messages.SystemUser);
    return DataCacheService.getUserName(userId);
  }
  renderReferenceYear() {
    const { rowData: { label } = {} } = this.props;
    if (!label)
      return (
        <span className="italic">
          <FormattedMessage {...messages.WithoutReferenceYear} />
        </span>
      );
    return label;
  }
  renderOwnCompany() {
    const { rowData: { name } = {} } = this.props;
    return <span>{name}</span>;
  }
  renderSimpleGroup() {
    const { rowData: { label } = {} } = this.props;
    return label;
  }

  renderLabel() {
    const { rowData: { rowType } = {} } = this.props;

    switch (rowType) {
      case -1:
        return <FormattedMessage {...messages.Totals} />;
      case 1:
        return this.renderRelationName();
      case 2:
        return this.renderAssignment();
      case 3:
        return this.renderResourceType();
      case 4:
        return this.renderResourceCategory();
      case 5:
        return this.renderResource();
      case 6:
        return this.renderTaskTemplate();
      case 8:
        return this.renderUserName();
      case 9:
      case 10:
      case 16:
      case 11:
        return this.renderSimpleGroup();
      case 12:
        return this.renderReferenceYear();
      case 13:
        return this.renderOwnCompany();
      case 15:
        return this.renderSubTaskTemplate();
      case 99:
        return this.renderDetailColumn();
      default:
        return `Unsupported type: ${rowType}`;
    }
  }

  renderInForfaitCheckbox() {
    const { rowData: { registrationInForfait } = {} } = this.props;
    if (typeof registrationInForfait === 'undefined' || registrationInForfait === null) return null;
    const checked =
      registrationInForfait === 'true' ||
      (registrationInForfait !== 'false' && !!registrationInForfait);
    return <i className={classnames('fa fa-fw', checked ? 'fa-check-square-o' : 'fa-square-o')} />;
  }

  render() {
    const { showDetails, rowData = {}, lastGroup, columnStatus } = this.props;
    const {
      rowType,
      rowDepth,
      potentialHours,
      potentialQuantity,
      potentialAmount,
      internalCost,
      notBillableHours,
      notBillableQuantity,
      notBillableAmount,
      billedHours,
      billedQuantity,
      billedAmount,
      billedInForfaitHours,
      billedInForfaitQuantity,
      billedInForfaitAmount,
      deletedHours,
      deletedQuantity,
      deletedAmount,
      neverInvoiceHours,
      neverInvoiceQuantity,
      neverInvoiceAmount,
      writtenOffHours,
      writtenOffQuantity,
      writtenOffAmount,
      deltaHours,
      deltaQuantity,
      deltaAmount,
      margin,

      registrationRemark,
      registrationInternalRemark,
      registrationInvoiceStatus,
    } = rowData;

    const isDetail = rowType === 99;
    const labelColspan =
      !showDetails || isDetail
        ? 1
        : 2 +
          (columnStatus.finalInvoicePermission ? 1 : 0) +
          (columnStatus.priceListPermission ? 1 : 0);

    return (
      <tr
        className={classnames(`indent-${rowDepth}`, {
          totals: rowType === -1,
          details: lastGroup === rowType,
        })}
      >
        <td colSpan={labelColspan}>{this.renderLabel()}</td>
        {isDetail && (
          <td>
            {registrationRemark} {registrationInternalRemark}
          </td>
        )}
        {isDetail && columnStatus.finalInvoicePermission && (
          <td>
            <FormattedListValue list="registrationinvoicestatus" id={registrationInvoiceStatus} />
          </td>
        )}
        {isDetail && columnStatus.priceListPermission && <td>{this.renderInForfaitCheckbox()}</td>}
        <HoursColumn value={potentialHours} className="border-dashed" />
        {columnStatus.potentialQuantity && <QuantityColumn value={potentialQuantity} />}
        {columnStatus.priceListPermission && <AmountColumn value={potentialAmount} />}
        {columnStatus.internalPricePermission && <AmountColumn value={internalCost} />}
        {columnStatus.notBillable && (
          <HoursColumn value={notBillableHours} className="border-dashed" />
        )}
        {columnStatus.notBillableQuantity && <QuantityColumn value={notBillableQuantity} />}
        {columnStatus.notBillable && <AmountColumn value={notBillableAmount} />}
        {columnStatus.billed && <HoursColumn value={billedHours} className="border-dashed" />}
        {columnStatus.billedQuantity && <QuantityColumn value={billedQuantity} />}
        {columnStatus.billed && <AmountColumn value={billedAmount} />}
        {columnStatus.inForfait && (
          <HoursColumn value={billedInForfaitHours} className="border-dashed" />
        )}
        {columnStatus.inForfaitQuantity && <QuantityColumn value={billedInForfaitQuantity} />}
        {columnStatus.inForfait && <AmountColumn value={billedInForfaitAmount} />}

        {columnStatus.deleted && <HoursColumn value={deletedHours} className="border-dashed" />}
        {columnStatus.deletedQuantity && <QuantityColumn value={deletedQuantity} />}
        {columnStatus.deleted && <AmountColumn value={deletedAmount} />}

        {columnStatus.neverInvoice && (
          <HoursColumn value={neverInvoiceHours} className="border-dashed" />
        )}
        {columnStatus.neverInvoiceQuantity && <QuantityColumn value={neverInvoiceQuantity} />}
        {columnStatus.neverInvoice && <AmountColumn value={neverInvoiceAmount} />}
        {columnStatus.writtenOff && (
          <HoursColumn value={writtenOffHours} className="border-dashed" />
        )}
        {columnStatus.writtenOffQuantity && <QuantityColumn value={writtenOffQuantity} />}
        {columnStatus.writtenOff && <AmountColumn value={writtenOffAmount} />}
        {columnStatus.billed && <HoursColumn value={deltaHours} className="border-dashed" />}
        {columnStatus.billedQuantity && <QuantityColumn value={deltaQuantity} />}
        {columnStatus.billed && <AmountColumn value={deltaAmount} />}
        {columnStatus.margin && <AmountColumn value={margin} className="border-dashed" />}
      </tr>
    );
  }
}

AmountColumn.propTypes = {
  value: PropTypes.number,
  className: PropTypes.string,
};
AmountColumn.defaultProps = {
  value: null,
  className: '',
};

HoursColumn.propTypes = {
  value: PropTypes.number,
  className: PropTypes.string,
};
HoursColumn.defaultProps = {
  value: null,
  className: '',
};
QuantityColumn.propTypes = {
  value: PropTypes.number,
  className: PropTypes.string,
};
QuantityColumn.defaultProps = {
  value: null,
  className: '',
};

RegistrationsPerRow.propTypes = {
  intl: intlShape.isRequired,
  showDetails: PropTypes.bool,
  showHours: PropTypes.bool,
  showAmounts: PropTypes.bool,
  showInternalPrice: PropTypes.bool,
  columnStatus: columnStatusShape.isRequired,
  rowData: PropTypes.shape({
    rowType: PropTypes.number.isRequired,
    rowDepth: PropTypes.number.isRequired,
    /* And many more properties */
  }).isRequired,
  lastGroup: PropTypes.number,
};
RegistrationsPerRow.defaultProps = {
  showDetails: false,
  lastGroup: null,
  showAmounts: false,
  showInternalPrice: false,
  showHours: false,
};

export default injectIntl(RegistrationsPerRow);
